import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Form,
  Row,
  VertButton,
  VertContainer,
  VertInput,
  VertModal,
  VertTypography,
  notification,
} from '@vertem-core/vertice-ds';
import { useSelector } from 'react-redux';
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';

// UTILS
import generateUUID from 'Utils/generateUUID';

const DefaultQuotationFormModal = ({ product, sku, onRequestSuccess }) => {
  const [useForm] = Form.useForm();
  const quotation = useSelector(({ Quotation }) => Quotation);
  const userProfile = useSelector((state) => state.oidc.user?.profile);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  // @ts-ignore
  const { callApi } = usePipeContext();

  const handleConfirmationClose = () => {
    setIsConfirmationOpen(false);
    clearFields();
    onRequestSuccess();
  };

  const clearFields = () => {
    useForm.resetFields([
      'name',
      'phone',
      'email',
      'observation',
      'quantity',
    ]);
  };

  const originalProductInfoBySku = (sku) => {
    return product?.skus?.find((product) => product?.originalId === sku);
  };

  const getOriginalId = product?.skus?.map(item => item.originalId)[0];

  const handleSubmit = (values) => {
  
    useForm
      .validateFields()
      .then(() => {
        const fieldsErros = useForm.getFieldsError();

        const dataIsValid = !fieldsErros.find((a) => a.errors.length > 0);

        const formValues = useForm.getFieldsValue();

        if (dataIsValid) {
          const products = [
            {
              name: product?.name,
              sku: String(getOriginalId),
              quantity: Number(values.quantity),
              unitPrice:
                (originalProductInfoBySku(sku)?.availability?.price ||
                originalProductInfoBySku(sku)?.availability?.regularPrice) ?? 0,
              vendorId: product?.vendor?.id.toString(),
              originalId:
                originalProductInfoBySku(sku)?.originalId.toString() ||
                product?.originalProductId.toString(),
              formId: product?.formId.toString(),
            },
          ];
        

          const { name, email, phone, observation } = formValues;

          const paramsToMap = {
            name,
            email,
            phone,
            observation,
            value:
              (originalProductInfoBySku(sku)?.availability?.price ||
                originalProductInfoBySku(sku)?.availability?.regularPrice) ?? 0,
          };

          const params = Object.entries(paramsToMap).map(([key, value]) => {
            
              switch (key) {
                case 'name':
                  return { key: 'Name', value };
                case 'email':
                  return { key: 'Email', value };
                case 'phone':
                  return { key: 'Telefone', value };
                case 'observation':
                  return { key: 'Observação', value };
                case 'value':
                  return { key: 'Valor', value: String(value) };
                default:
                  return {
                    key,
                    value,
                  }
                  
              }
            
          });
       

          

          callApi({
            symbol: 'ADD_QUOTATION',
            alias: generateUUID(),
            payload: {
              products,
              supplierId: product?.vendor?.id,
              participantId: userProfile['custom:participant_id'],
              params,
            },
          });
        }
      })
      .catch((error) => {
        console.log('erro cotação', error);
      });
  };
  
  useEffect(() => {
    if (quotation && quotation.isLoaded && quotation.idQuotation > 0) {
      setIsConfirmationOpen(true);
    }
    if (quotation.isError) {
      notification.error({
        message:
          'Não foi possível registrar a Cotação, entre em contato com a central!',
      });
    }
  }, [quotation]);

  useEffect(() => {
    clearFields();
  }, [product?.vendor?.id]);

  return (
    <>
      <Form
        name="FormsPersonalData"
        form={useForm}
        autoComplete="off"
        layout="vertical"
        onFinish={
        (values)=>{
        handleSubmit(values)
        }
      }
      >
        <VertContainer spacingX="small">
          <Row>
            <Col>
              <VertTypography.Paragraph
                className="margin-bottom-extra-small"
                strong
              >
                Dados Pessoais
              </VertTypography.Paragraph>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item
                name="name"
                validateTrigger={['onBlur', 'onChange']}
                rules={[
                  { required: true, message: 'Nome precisa ser preenchido.' },
                ]}
              >
                <VertInput label="Nome Completo*" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16]}>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                name="phone"
                validateTrigger={['onBlur', 'onChange']}
                rules={[
                  {
                    required: true,
                    message: 'Telefone/celular precisa ser preenchido.',
                  },
                  {
                    pattern: /^\([0-9]{2}\) [0-9]{5}-[0-9]{4}$/g,
                    message: 'Celular inválido.',
                  },
                ]}
              >
                <VertInput mask="(00) 00000-0000" placeholder="Celular*" />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                name="email"
                validateTrigger={['onBlur', 'onChange']}
                rules={[
                  { required: true, message: 'E-mail precisa ser preenchido.' },
                  {
                    type: 'email',
                    message: 'E-mail inválido.',
                  },
                ]}
              >
                <VertInput label="E-mail*" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <VertTypography.Paragraph
                className="margin-bottom-extra-small"
                strong
              >
                Informações para cotação
              </VertTypography.Paragraph>
            </Col>
          </Row>
          <Row gutter={[16]}>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                name="quantity"
                validateTrigger={['onBlur', 'onChange']}
              
                rules={[
                  { required: true, message: 'Quantidade é obrigatória.' },
                  {
                    pattern: /^[0-9]*$/g,
                    message: 'Quantidade inválida.',
                  },
                ]}
              >
                <VertInput maxLength={15} label="Quantidade*" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16]}>
            <Col lg={24} md={24} sm={24} xs={24}>
              <Form.Item
                name="observation"
                validateTrigger={['onBlur', 'onChange']}
              >
                <VertInput.TextArea
                  className="modal-text-area"
                  placeholder="Observação"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end">
            <Col lg={12} md={12} sm={12} xs={24}>
              <VertButton
                type="highlight"
                size="large"
                
                key="submit"
                htmlType="submit"
                loading={quotation?.isLoading}
                className="col-12"
              >
                Enviar
              </VertButton>
            </Col>
          </Row>
        </VertContainer>
      </Form>
      <VertModal
        open={isConfirmationOpen}
        className="quotation-options-modal-confirmation"
        onCancel={handleConfirmationClose}
        spacing="regular"
      >
        <VertContainer>
          <Row justify="center" align="middle" className="info-primary">
            <VertTypography.Paragraph strong>
              Cotação enviada com sucesso
            </VertTypography.Paragraph>
          </Row>
          <Row verticalSpacing="small">
            <Col>
              <VertTypography.Text>
                Agradecemos o contato. Em breve enviaremos a sua cotação para o
                e-mail: {useForm.getFieldValue('email')}
              </VertTypography.Text>
            </Col>
          </Row>
          <VertTypography.Text>
            Solicitação: {quotation?.idQuotation}
          </VertTypography.Text>
          <Row className="margin-top-regular" justify="end">
            <Col lg={12} md={12} sm={12} xs={24}>
              <VertButton
                type="highlight"
                size="large"
                onClick={handleConfirmationClose}
                className="col-12"
              >
                Entendi
              </VertButton>
            </Col>
          </Row>
        </VertContainer>
      </VertModal>
    </>
  );
};

DefaultQuotationFormModal.propTypes = {
  product: PropTypes.object.isRequired,
  sku: PropTypes.string,
  onRequestSuccess: PropTypes.func,
};

export default DefaultQuotationFormModal;
